import type { StationPrerequisite } from './stationStepReadiness'

/**
 * SCRUM-1032 QC round 2, items 2 / 7 / 8 — what a step still needs before the
 * wizard lets the user past it.
 *
 * "Visited" used to be enough. A controller could be walked end to end with
 * nothing entered, and the rail ticked every step on the way. These are the
 * steps whose data the rest of the setup is built on; the others never hold
 * the user — input and SDI-12 decoders default to their address, geotagging is
 * optional, and so is ET Reference (Jonathan, 22 Sep). The rail still flags an
 * empty optional step; it just does not stand in the way.
 */
export const MUST_FILL_STEP_KEYS: ReadonlySet<string> = new Set([
  'decodersOutput',
  'hydraulic',
  'plantSoilEmitter',
  'areas',
  'station',
])

/** Named rather than worded: the caller builds the sentence from keys. */
export type StepGap =
  | { kind: 'decoderPurpose'; count: number }
  | { kind: 'mainline' }
  | { kind: 'prerequisites'; missing: StationPrerequisite[] }
  /**
   * Decoders marked as stations that have no station yet, counted from the
   * server (QC round 7 review) — used when the Station grid is not open.
   */
  | { kind: 'stationsMissing'; count: number }
  /** Areas with no sub-area under them, by name (QC round 4, item 6). */
  | { kind: 'areaWithoutSubArea'; names: string[] }
  /** The step's own grid said so, in its own words. */
  | { kind: 'reported'; reason: string }
  /**
   * A count the gate needs has not been read yet, or its request failed. It
   * holds like any other gap: read as "nothing missing", an unread mainline
   * count let Next and Finish through with no mainline (wizard review).
   */
  | { kind: 'checking' }

/**
 * What the wizard knows from the server, per step. `undefined` is "not read
 * yet": on a must-fill step that holds the user as `checking` until the count
 * arrives; on an optional step it never does.
 */
export interface GuidedStepData {
  outputDecodersWithoutPurpose?: number
  /** Input and SDI-12 decoders only need a name (Q2). */
  inputDecodersWithoutName?: number
  sdi12DecodersWithoutName?: number
  /** Geotagging is optional: this flags the step, it never holds it. */
  unpinnedStations?: number
  mainlines?: number
  etReferences?: number
  plants?: number
  soils?: number
  emitters?: number
  areas?: number
  subAreas?: number
  /**
   * The areas that have no sub-area under them, by name. Undefined until the
   * area list has been read, like every other count here.
   */
  areasWithoutSubArea?: string[]
  /** Output decoders whose purpose makes them a station. */
  stationDecoders?: number
  /** Stations the server holds. */
  stations?: number
}

/**
 * SCRUM-1032 QC round 7 review — the Station gap from the server, for when the
 * Station grid has not said anything this session.
 *
 * The step was judged only on what its grid reported, and a grid that was
 * never mounted reports nothing: a rail jump to Geotag, a reload while on
 * Geotag, or purposes set after the last visit all let Finish complete the
 * controller with stations never written. Counts, not addresses: a station
 * left on a repurposed decoder can hide one missing station, which is the
 * price of not pulling a thousand stations to check one gate.
 */
export const missingStationCount = (data: GuidedStepData): number | undefined => {
  if (data.stationDecoders === undefined || data.stations === undefined) {
    return undefined
  }
  return Math.max(0, data.stationDecoders - data.stations)
}

const isZero = (value: number | undefined) => value !== undefined && value <= 0
const CHECKING: StepGap = { kind: 'checking' }
const anyUnknown = (...values: unknown[]) => values.some((v) => v === undefined)

/**
 * The gap for one step, or null when nothing holds it.
 *
 * `reported` is what the step's grid last said. For the decoder and station
 * steps it is the better answer — the grid counts edits that have not reached
 * the server yet — so when the grid has spoken (`reported !== undefined`),
 * its answer is final, including a null.
 */
export const stepGap = (
  stepKey: string,
  data: GuidedStepData,
  reported?: string | null,
): StepGap | null => {
  if (!MUST_FILL_STEP_KEYS.has(stepKey)) return null

  switch (stepKey) {
    case 'decodersOutput': {
      if (reported !== undefined) {
        return reported ? { kind: 'reported', reason: reported } : null
      }
      const count = data.outputDecodersWithoutPurpose
      if (count === undefined) return CHECKING
      return count > 0 ? { kind: 'decoderPurpose', count } : null
    }
    case 'station': {
      // The open grid knows best — it counts drafts not written yet. Its word
      // is final when it has given one (`reported !== undefined`).
      if (reported !== undefined) {
        return reported ? { kind: 'reported', reason: reported } : null
      }
      const count = missingStationCount(data)
      if (count === undefined) return CHECKING
      return count > 0 ? { kind: 'stationsMissing', count } : null
    }
    case 'hydraulic':
      if (data.mainlines === undefined) return CHECKING
      return isZero(data.mainlines) ? { kind: 'mainline' } : null
    case 'plantSoilEmitter': {
      if (anyUnknown(data.plants, data.soils, data.emitters)) return CHECKING
      const missing: StationPrerequisite[] = []
      if (isZero(data.plants)) missing.push('plant')
      if (isZero(data.soils)) missing.push('soil')
      if (isZero(data.emitters)) missing.push('emitter')
      return missing.length ? { kind: 'prerequisites', missing } : null
    }
    case 'areas': {
      if (anyUnknown(data.areas, data.subAreas, data.areasWithoutSubArea)) {
        return CHECKING
      }
      if (isZero(data.areas)) return { kind: 'prerequisites', missing: ['area'] }
      if (isZero(data.subAreas)) {
        return { kind: 'prerequisites', missing: ['subArea'] }
      }
      // Every area needs one of its own, not one somewhere on the controller
      // (QC round 4, item 6).
      const empty = data.areasWithoutSubArea ?? []
      if (empty.length > 0) {
        return { kind: 'areaWithoutSubArea', names: empty }
      }
      return null
    }
    default:
      return null
  }
}

/**
 * The first step, in wizard order, that still has a gap — the furthest the
 * user can go. -1 when nothing holds them.
 */
export const firstBlockingStep = (
  stepKeys: readonly string[],
  gapOf: (stepKey: string) => StepGap | null,
): number => stepKeys.findIndex((key) => gapOf(key) !== null)

/**
 * Whether moving from `fromIndex` to `toIndex` is allowed. Backwards always
 * is; forwards stops at the first step that still has a gap, wherever it is —
 * including one behind the user, left incomplete on an earlier visit.
 */
export const canMoveTo = (
  fromIndex: number,
  toIndex: number,
  blockingIndex: number,
) => toIndex <= fromIndex || blockingIndex < 0 || toIndex <= blockingIndex

/** Top-level pipes. `/api/pipe` nests sub-pipes under their mainline. */
export const countMainlines = (pipes: unknown): number =>
  (Array.isArray(pipes) ? pipes : []).filter(
    (pipe: any) =>
      !(Number(pipe?.parentId ?? 0) > 0) && Number(pipe?.level ?? 1) <= 1,
  ).length

/** Decoders with no purpose set. The purpose is required on every output. */
export const countWithoutPurpose = (decoders: unknown): number =>
  (Array.isArray(decoders) ? decoders : []).filter(
    (decoder: any) => !hasPurpose(decoder),
  ).length

/**
 * The purpose name decides when the row carries one: a row staged in the grid
 * holds the edited name next to the id it was loaded with, so the id alone
 * would call a cleared purpose set.
 */
export const hasPurpose = (decoder: any) =>
  decoder && 'decoderType' in decoder
    ? String(decoder.decoderType ?? '').trim() !== ''
    : Number(decoder?.decoderTypeId) > 0

/**
 * QC round 2, item 4 — whether a step's data is in, for the rail's marker.
 *
 * The must-fill steps are complete when nothing holds them. The others have a
 * softer bar: input and SDI-12 decoders need a name (a type with none is
 * complete — there is nothing to enter), and geotagging wants every station
 * pinned. An unread count is not marked either way: `checking` holds Next but
 * does not paint the rail red while the counts load.
 */
export const stepComplete = (
  stepKey: string,
  data: GuidedStepData,
  reported?: string | null,
): boolean => {
  switch (stepKey) {
    // Optional, like geotagging: flagged on the rail when empty, never blocking.
    case 'etReference':
      return !isZero(data.etReferences)
    case 'decodersInput':
      return !(Number(data.inputDecodersWithoutName ?? 0) > 0)
    case 'decodersSdi12':
      return !(Number(data.sdi12DecodersWithoutName ?? 0) > 0)
    case 'geotag':
      return !(Number(data.unpinnedStations ?? 0) > 0)
    default: {
      const gap = stepGap(stepKey, data, reported)
      return gap === null || gap.kind === 'checking'
    }
  }
}

/** The server counts the wizard re-reads on a timer, and the step that moves each. */
export type PolledCount = 'plants' | 'soils' | 'emitters' | 'areas' | 'mainlines'

const COUNT_OWNER: Record<PolledCount, string> = {
  plants: 'plantSoilEmitter',
  soils: 'plantSoilEmitter',
  emitters: 'plantSoilEmitter',
  areas: 'areas',
  mainlines: 'hydraulic',
}

/**
 * Whether a count is worth re-reading on the timer right now.
 *
 * The grids write through their own APIs and invalidate nothing, so a gate on
 * one of these counts is only re-read by polling. It used to poll all six
 * from the reset step on, every 4 s, for as long as the Station prerequisites
 * were missing (wizard review). Now only the step that can change a count
 * polls it, and only while that count is holding something: the Station
 * prerequisites, or the open step's own gap. Every count is still re-read on
 * each step change.
 */
export const shouldPollCount = (
  count: PolledCount,
  {
    activeKey,
    stationReady,
    blockingKey,
    value,
  }: {
    activeKey: string | null
    stationReady: boolean
    blockingKey?: string
    /** The count as last read; undefined while it has not been. */
    value?: number
  },
): boolean => {
  const owner = COUNT_OWNER[count]
  if (!activeKey || owner !== activeKey) return false
  // Plants, soils and emitters only gate on "at least one". Once there is one,
  // the chips' own re-read after a write (shared cache key) and the delete
  // invalidation keep the count current. Polling past that re-read all three
  // lists every 4 s for as long as areas were missing (QC 30 Oct, step 7).
  if (owner === 'plantSoilEmitter' && value !== undefined && value > 0) return false
  if (blockingKey === owner) return true
  // Plants, soils, emitters and areas are also what opens the Station step.
  return count !== 'mainlines' && !stationReady
}

export type RailStepStatus =
  | 'current'
  | 'done'
  | 'incomplete'
  | 'optional'
  | 'upcoming'

/**
 * The rail marker. A tick used to mean "walked past"; now it means the data is
 * in. Steps not reached yet keep their number, whatever their data says — a
 * tick on a step the user has never opened would claim a check nobody made.
 *
 * An empty step that is not must-fill answers `optional`, not `incomplete`:
 * the red exclamation mark reads as something gone wrong, which is the wrong
 * thing to say about a step the user is free to leave empty (ET Reference,
 * geotagging — Jonathan, 22 Sep).
 */
export const railStepStatus = ({
  isActive,
  visited,
  complete,
  optional = false,
}: {
  isActive: boolean
  visited: boolean
  complete: boolean
  optional?: boolean
}): RailStepStatus => {
  if (isActive) return 'current'
  if (!visited) return 'upcoming'
  if (complete) return 'done'
  return optional ? 'optional' : 'incomplete'
}

/** Decoders with no name. The grid shows the address when there is none. */
export const countWithoutName = (decoders: unknown): number =>
  (Array.isArray(decoders) ? decoders : []).filter(
    (decoder: any) => String(decoder?.name ?? '').trim() === '',
  ).length

/**
 * The decoder row a purpose count should judge, given what is staged and what
 * this sitting has already written.
 *
 * Order matters. A saved row drops its staged edit as soon as its request
 * returns, but the decoder list is only re-read at the end of the pass, so in
 * between the server copy still has no purpose. Counting that copy made the
 * "without purpose" total climb while a save ran instead of falling
 * (SCRUM-1032, QC 22 Sep). `written` holds the purpose last sent for the row —
 * including an empty string, so clearing one is not masked.
 */
export const resolvePurposeRow = (
  row: any,
  staged?: any,
  written?: string,
): any => {
  if (staged) return staged
  if (written === undefined) return row
  return { ...row, decoderType: written }
}
